// /opt/isp_erp/frontend/src/components/tickets/CloseCambioEquipoDialog.jsx

import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Box,
  Alert,
  CircularProgress,
  Grid,
  Paper,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Divider,
  InputAdornment,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Switch,
  FormControlLabel
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  SwapHoriz as SwapHorizIcon,
  Search,
  Clear,
  ExpandMore,
  Inventory,
  LocationOn as LocationIcon,
  MyLocation as MyLocationIcon,
  PhotoCamera as PhotoIcon,
  Download as DownloadIcon,
  CheckCircle as CheckIcon
} from '@mui/icons-material';
import { ticketsAPI } from '../../api/tickets.api';
import { warehouseAPI } from '../../api/warehouse.api';
import api from '../../api/axios';
import FotoUploader from './FotoUploader';

const CloseCambioEquipoDialog = ({ open, onClose, ticket, onSuccess }) => {
  const [materiales, setMateriales] = useState([]);
  const [nuevoMaterial, setNuevoMaterial] = useState({
    material_id: '',
    cantidad: 1,
    observaciones: ''
  });
  const [motivo, setMotivo] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Estados para éxito y PDF
  const [showSuccess, setShowSuccess] = useState(false);
  const [pdfBase64, setPdfBase64] = useState(null);
  const [ticketNumber, setTicketNumber] = useState('');
  const [ticketId, setTicketId] = useState(null);

  // Estados para fotos
  const [fotosBase64, setFotosBase64] = useState([]);

  // Estados para ubicación
  const [ubicacion, setUbicacion] = useState(null);
  const [obteniendoUbicacion, setObteniendoUbicacion] = useState(false);
  const [errorUbicacion, setErrorUbicacion] = useState('');
  const [usarUbicacion, setUsarUbicacion] = useState(true);

  // Estados para equipos
  const [equiposNuevos, setEquiposNuevos] = useState([]);
  const [equiposViejos, setEquiposViejos] = useState([]);
  const [equiposEnTransito, setEquiposEnTransito] = useState([]);
  const [equiposFiltrados, setEquiposFiltrados] = useState([]);
  const [selectedEquipo, setSelectedEquipo] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [cargandoEquipos, setCargandoEquipos] = useState(false);
  const [expandedEquipos, setExpandedEquipos] = useState(false);

  // Catálogo de materiales
  const [catalogoMateriales, setCatalogoMateriales] = useState([]);
  const [expandedMateriales, setExpandedMateriales] = useState(false);

  // Función para descargar PDF
  const handleDownloadPDF = () => {
    if (!pdfBase64) return;

    try {
      const byteCharacters = atob(pdfBase64);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `cambio_equipo_${ticketNumber || ticketId}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error descargando PDF:', error);
      setError('Error al descargar el PDF');
    }
  };

  const handleCloseDialog = () => {
    setShowSuccess(false);
    setPdfBase64(null);
    setTicketNumber('');
    setTicketId(null);
    onClose();
  };

  // Función para obtener ubicación
  const obtenerUbicacion = () => {
    if (!navigator.geolocation) {
      setErrorUbicacion('Geolocalización no soportada');
      setUsarUbicacion(false);
      return;
    }

    setObteniendoUbicacion(true);
    setErrorUbicacion('');

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const ubicacionData = {
          latitud: position.coords.latitude,
          longitud: position.coords.longitude,
          precision: position.coords.accuracy
        };
        setUbicacion(ubicacionData);
        setObteniendoUbicacion(false);
      },
      (error) => {
        console.error('Error de geolocalización:', error);
        let mensaje = '';
        switch(error.code) {
          case error.PERMISSION_DENIED:
            mensaje = 'Permiso denegado. Activa la ubicación en tu dispositivo.';
            break;
          case error.POSITION_UNAVAILABLE:
            mensaje = 'No se pudo obtener la ubicación. Verifica tu GPS.';
            break;
          case error.TIMEOUT:
            mensaje = 'Tiempo de espera agotado. Intenta nuevamente.';
            break;
          default:
            mensaje = 'Error al obtener ubicación.';
        }
        setErrorUbicacion(mensaje);
        setObteniendoUbicacion(false);
        setUsarUbicacion(false);
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 0
      }
    );
  };

  // Cargar equipos en tránsito del técnico asignado al ticket
  useEffect(() => {
    if (open && ticket) {
      setShowSuccess(false);
      setPdfBase64(null);
      setTicketNumber('');

      cargarEquiposDelTecnico();
      cargarMateriales();

      if (usarUbicacion) {
        obtenerUbicacion();
      }

      if (ticket.materials_used?.equipos_viejos) {
        setEquiposViejos(ticket.materials_used.equipos_viejos);
      } else if (ticket.old_equipment_sn) {
        setEquiposViejos([{ sn: ticket.old_equipment_sn, motivo: '' }]);
      }

      if (ticket.materials_used?.equipos_nuevos) {
        setEquiposNuevos(ticket.materials_used.equipos_nuevos);
      }
    }
  }, [open, ticket]);

  // Filtrar equipos según búsqueda
  useEffect(() => {
    if (searchTerm && equiposEnTransito.length > 0) {
      const filtrados = equiposEnTransito.filter(e =>
        e.sn?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        e.modelo?.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setEquiposFiltrados(filtrados);
    } else {
      setEquiposFiltrados(equiposEnTransito);
    }
  }, [searchTerm, equiposEnTransito]);

  const cargarMateriales = async () => {
    try {
      const response = await api.get('/warehouse/materiales');
      setCatalogoMateriales(response.data || []);
    } catch (error) {
      console.error('Error cargando materiales:', error);
    }
  };

  const cargarEquiposDelTecnico = async () => {
    setCargandoEquipos(true);
    setError('');
    try {
      const tecnicoIdAsignado = ticket.assigned_technician_id;

      if (!tecnicoIdAsignado) {
        setError('El ticket no tiene técnico asignado');
        setCargandoEquipos(false);
        return;
      }

      const response = await warehouseAPI.listarEquipos({
        estado: 'EN_TRANSITO'
      });

      const todosEquipos = response?.data || [];

      const equiposDelTecnico = todosEquipos.filter(equipo => {
        const poseedorTipo = equipo.poseedor?.tipo;
        const poseedorId = equipo.poseedor?.id;
        return poseedorTipo &&
               (poseedorTipo === 'Técnico' || poseedorTipo === 'tecnico') &&
               parseInt(poseedorId) === parseInt(tecnicoIdAsignado);
      });

      setEquiposEnTransito(equiposDelTecnico);
      setEquiposFiltrados(equiposDelTecnico);

      if (equiposDelTecnico.length === 0 && ticket.status !== 'cerrado') {
        setError(`No se encontraron equipos en tránsito para el técnico ID ${tecnicoIdAsignado}`);
      } else if (equiposDelTecnico.length === 0 && ticket.status === 'cerrado') {
        console.log('ℹ️ El ticket ya está cerrado, los equipos ya fueron instalados');
        setError('');
      }
    } catch (error) {
      console.error('Error cargando equipos:', error);
      setError(error?.response?.data?.detail || 'Error al cargar los equipos disponibles');
    } finally {
      setCargandoEquipos(false);
    }
  };

  const agregarEquipoViejo = () => {
    const sn = prompt('Ingrese el número de serie del equipo viejo a retirar:');
    if (sn && sn.trim()) {
      if (equiposViejos.some(e => e.sn === sn.trim())) {
        setError('Este equipo ya ha sido agregado');
        return;
      }
      const motivoEquipo = prompt('Motivo del retiro (opcional):');
      setEquiposViejos([...equiposViejos, {
        sn: sn.trim(),
        motivo: motivoEquipo || ''
      }]);
    }
  };

  const eliminarEquipoViejo = (index) => {
    setEquiposViejos(equiposViejos.filter((_, i) => i !== index));
  };

  const agregarEquipo = () => {
    if (!selectedEquipo) {
      setError('Seleccione un equipo');
      return;
    }
    if (equiposNuevos.some(e => e.sn === selectedEquipo.sn)) {
      setError('Este equipo ya ha sido agregado');
      return;
    }
    setEquiposNuevos([...equiposNuevos, {
      sn: selectedEquipo.sn,
      tipo: 'ONU',
      modelo: selectedEquipo.modelo || 'DESCONOCIDO'
    }]);
    setSelectedEquipo(null);
    setSearchTerm('');
    setError('');
  };

  const eliminarEquipo = (index) => {
    setEquiposNuevos(equiposNuevos.filter((_, i) => i !== index));
  };

  const actualizarTipoEquipo = (index, tipo) => {
    const nuevos = [...equiposNuevos];
    nuevos[index].tipo = tipo;
    setEquiposNuevos(nuevos);
  };

  // ✅ CORREGIDO: Agregar material con logs
  const agregarMaterial = () => {
    if (!nuevoMaterial.material_id) {
      setError('Seleccione un material');
      return;
    }
    if (!nuevoMaterial.cantidad || nuevoMaterial.cantidad < 1) {
      setError('Ingrese una cantidad válida');
      return;
    }

    const materialInfo = catalogoMateriales.find(m => m.id === nuevoMaterial.material_id);
    if (!materialInfo) {
      setError('Material no encontrado');
      return;
    }

    const nuevoMaterialObj = {
      nombre: materialInfo.nombre,
      cantidad: nuevoMaterial.cantidad,
      observaciones: nuevoMaterial.observaciones || ''
    };

    console.log('✅ Agregando material:', nuevoMaterialObj);
    console.log('📦 Materiales antes de agregar:', materiales);

    setMateriales([...materiales, nuevoMaterialObj]);
    setNuevoMaterial({ material_id: '', cantidad: 1, observaciones: '' });
    setError('');

    // ✅ Verificar después de agregar
    setTimeout(() => {
      console.log('📦 Materiales después de agregar:', materiales);
    }, 100);
  };

  const eliminarMaterial = (index) => {
    setMateriales(materiales.filter((_, i) => i !== index));
  };

  // ==================== handleSubmit CORREGIDO ====================
  const handleSubmit = async () => {
    // ✅ VALIDAR MATERIALES ANTES DE ENVIAR
    console.log('📦 MATERIALES FINALES antes de enviar:', JSON.stringify(materiales, null, 2));

    if (materiales.length === 0) {
      setError('Debe agregar al menos un material');
      return;
    }

    if (equiposNuevos.length === 0) {
      setError('Debe seleccionar al menos un equipo nuevo a instalar');
      return;
    }

    setLoading(true);
    setError('');

    try {
      let ubicacionData = null;
      if (usarUbicacion && ubicacion) {
        ubicacionData = {
          latitud: ubicacion.latitud,
          longitud: ubicacion.longitud,
          precision: ubicacion.precision
        };
      }

      const equiposViejosFinal = equiposViejos.length > 0
        ? equiposViejos
        : (ticket.materials_used?.equipos_viejos ||
           (ticket.old_equipment_sn ? [{ sn: ticket.old_equipment_sn, motivo: motivo }] : []));

      console.log('📦 MATERIALES antes de enviar:', JSON.stringify(materiales, null, 2));
      console.log('📦 EQUIPOS NUEVOS antes de enviar:', JSON.stringify(equiposNuevos, null, 2));
      console.log('📦 EQUIPOS VIEJOS antes de enviar:', JSON.stringify(equiposViejosFinal, null, 2));

      const result = await ticketsAPI.closeCambioEquipoTicket(
        ticket.id,
        equiposViejosFinal,
        equiposNuevos,
        materiales,  // ✅ Enviar materiales
        motivo || ticket?.description || 'Cambio de equipo',
        fotosBase64,
        ubicacionData
      );

      if (result.pdf_generado && result.pdf_base64) {
        setPdfBase64(result.pdf_base64);
        setTicketNumber(result.ticket || ticket.ticket_number);
        setTicketId(ticket.id);
      }

      setShowSuccess(true);
      if (onSuccess) onSuccess();

    } catch (err) {
      console.error('Error closing ticket:', err);
      setError(err.response?.data?.detail || 'Error al cerrar el ticket');
      setShowSuccess(false);
    } finally {
      setLoading(false);
    }
  };

  // Pantalla de éxito
  if (showSuccess) {
    return (
      <Dialog open={open} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ textAlign: 'center' }}>
          <CheckIcon sx={{ fontSize: 60, color: 'success.main' }} />
          <Typography variant="h5" color="success.main" sx={{ mt: 1 }}>
            ¡Cambio de Equipo Completado!
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ textAlign: 'center', py: 2 }}>
            <Typography variant="body1" gutterBottom>
              Ticket <strong>{ticketNumber || ticket?.ticket_number}</strong> cerrado exitosamente.
            </Typography>
            {pdfBase64 && (
              <Button
                variant="contained"
                color="primary"
                size="large"
                startIcon={<DownloadIcon />}
                onClick={handleDownloadPDF}
                sx={{ mt: 3, mb: 2, px: 4 }}
                fullWidth
              >
                📄 Descargar PDF del Cambio
              </Button>
            )}
            {!pdfBase64 && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                El PDF se generará automáticamente.
              </Typography>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'center', pb: 3 }}>
          <Button variant="outlined" onClick={handleCloseDialog} size="large">
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>
    );
  }

  if (!ticket) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        Completar Cambio de Equipo
        <Typography variant="body2" color="text.secondary">
          Ticket: {ticket.ticket_number} - {ticket.title}
        </Typography>
      </DialogTitle>

      <DialogContent>
        <Box sx={{ mb: 3 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Alert severity="warning" sx={{ mb: 2 }}>
                <strong>Equipo(s) a RETIRAR:</strong>{' '}
                {equiposViejos.length > 0
                  ? equiposViejos.map(e => e.sn).join(', ')
                  : (ticket.materials_used?.equipos_viejos?.map(e => e.sn).join(', ') ||
                     ticket.old_equipment_sn || 'No especificado')}
              </Alert>
            </Grid>
            <Grid item xs={12} md={6}>
              <Alert severity="success" sx={{ mb: 2 }}>
                <strong>Equipo(s) nuevo(s) a instalar:</strong> {equiposNuevos.length} seleccionados
              </Alert>
            </Grid>
          </Grid>
          <Alert severity="info">
            <strong>🔄 Proceso de Cambio:</strong>
            <br />• Seleccione los equipos nuevos que instalará (deben estar en tránsito con el técnico asignado)
            <br />• Los equipos viejos cambiarán a estado <strong>RETIRO_PENDIENTE</strong>
            <br />• Los equipos nuevos cambiarán a estado <strong>INSTALADO</strong>
            <br />• El técnico deberá devolver los equipos viejos a bodega
            <br />• Los materiales se descontarán del stock del técnico
          </Alert>
        </Box>

        {/* FOTOS */}
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle1" sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <PhotoIcon color="primary" />
            Evidencia Fotográfica
          </Typography>
          <FotoUploader onFotosChange={setFotosBase64} maxFotos={9} />
        </Box>

        {/* UBICACIÓN */}
        <Paper sx={{ p: 2, mb: 2, bgcolor: '#f5f5f5' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="subtitle1" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <LocationIcon color="primary" />
              Ubicación del Cambio
            </Typography>
            <FormControlLabel
              control={<Switch checked={usarUbicacion} onChange={(e) => setUsarUbicacion(e.target.checked)} size="small" />}
              label="Guardar ubicación"
            />
          </Box>
          {usarUbicacion && (
            <>
              {!ubicacion && !obteniendoUbicacion && !errorUbicacion && (
                <Button size="small" variant="outlined" startIcon={<MyLocationIcon />} onClick={obtenerUbicacion} fullWidth>
                  Obtener ubicación actual
                </Button>
              )}
              {obteniendoUbicacion && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CircularProgress size={20} />
                  <Typography variant="body2">Obteniendo ubicación...</Typography>
                </Box>
              )}
              {errorUbicacion && (
                <Alert severity="warning" size="small" sx={{ mt: 1 }}>{errorUbicacion}</Alert>
              )}
              {ubicacion && (
                <Alert severity="success" sx={{ mt: 1 }} icon={<MyLocationIcon />}>
                  <Typography variant="caption" display="block">
                    Lat: {ubicacion.latitud.toFixed(6)} | Lng: {ubicacion.longitud.toFixed(6)}
                  </Typography>
                  {ubicacion.precision && (
                    <Typography variant="caption" display="block" color="text.secondary">
                      Precisión: ±{Math.round(ubicacion.precision)}m
                    </Typography>
                  )}
                </Alert>
              )}
            </>
          )}
        </Paper>

        {/* EQUIPOS VIEJOS EDITABLES */}
        <Accordion defaultExpanded>
          <AccordionSummary expandIcon={<ExpandMore />}>
            <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Inventory sx={{ mr: 1, verticalAlign: 'middle' }} />
              Equipos VIEJOS a Retirar ({equiposViejos.length})
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Button variant="outlined" size="small" startIcon={<AddIcon />} onClick={agregarEquipoViejo} sx={{ mb: 2 }}>
              Agregar equipo viejo
            </Button>
            {equiposViejos.length === 0 ? (
              <Typography variant="caption" color="text.secondary">
                No hay equipos viejos registrados. Use el botón para agregar o se tomarán los del ticket.
              </Typography>
            ) : (
              equiposViejos.map((eq, idx) => (
                <Paper key={idx} sx={{ p: 2, mb: 2 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box>
                      <Typography fontWeight="bold">{eq.sn}</Typography>
                      {eq.motivo && (
                        <Typography variant="caption" color="text.secondary" display="block">
                          Motivo: {eq.motivo}
                        </Typography>
                      )}
                    </Box>
                    <IconButton size="small" color="error" onClick={() => eliminarEquipoViejo(idx)}>
                      <DeleteIcon />
                    </IconButton>
                  </Box>
                </Paper>
              ))
            )}
            {equiposViejos.length === 0 && ticket.materials_used?.equipos_viejos && (
              <Alert severity="info" sx={{ mt: 1 }}>
                Equipos viejos del ticket: {ticket.materials_used.equipos_viejos.map(e => e.sn).join(', ')}
              </Alert>
            )}
          </AccordionDetails>
        </Accordion>

        {/* Selección de equipos nuevos */}
        <Accordion expanded={expandedEquipos} onChange={() => setExpandedEquipos(!expandedEquipos)}>
          <AccordionSummary expandIcon={<ExpandMore />}>
            <Typography variant="h6">
              <Inventory sx={{ mr: 1, verticalAlign: 'middle' }} />
              Equipos NUEVOS a Instalar ({equiposNuevos.length})
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Typography variant="caption" color="warning.main" sx={{ mb: 2, display: 'block' }}>
              Solo se muestran equipos que tiene el técnico asignado (EN_TRANSITO)
            </Typography>

            <TextField
              fullWidth
              placeholder="Buscar equipo por SN o modelo..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              size="small"
              sx={{ mb: 2 }}
              InputProps={{
                startAdornment: <InputAdornment position="start"><Search /></InputAdornment>,
                endAdornment: searchTerm && (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setSearchTerm('')}><Clear /></IconButton>
                  </InputAdornment>
                )
              }}
            />

            {cargandoEquipos ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}><CircularProgress /></Box>
            ) : (
              <>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>Equipos disponibles:</Typography>
                <List dense sx={{ maxHeight: 200, overflow: 'auto', border: '1px solid #e0e0e0', borderRadius: 1, mb: 2 }}>
                  {equiposFiltrados.length === 0 ? (
                    <ListItem>
                      <ListItemText
                        primary="No hay equipos en tránsito disponibles para este técnico"
                        secondary="Asegúrate de que el técnico tenga equipos asignados en tránsito"
                      />
                    </ListItem>
                  ) : (
                    equiposFiltrados.map(equipo => (
                      <ListItem
                        key={equipo.id}
                        button
                        onClick={() => setSelectedEquipo(equipo)}
                        selected={selectedEquipo?.id === equipo.id}
                        disabled={equiposNuevos.some(e => e.sn === equipo.sn)}
                        sx={{
                          '&:hover': { bgcolor: '#e8f5e9' },
                          bgcolor: selectedEquipo?.id === equipo.id ? '#e8f5e9' : 'transparent'
                        }}
                      >
                        <ListItemText
                          primary={
                            <Box display="flex" alignItems="center" gap={1}>
                              <Typography fontWeight="bold">{equipo.sn}</Typography>
                              <Chip label={equipo.modelo || 'Sin modelo'} size="small" variant="outlined" />
                            </Box>
                          }
                          secondary={`Estado: EN_TRANSITO`}
                        />
                        {equiposNuevos.some(e => e.sn === equipo.sn) && (
                          <Chip label="Ya agregado" color="success" size="small" />
                        )}
                      </ListItem>
                    ))
                  )}
                </List>

                {selectedEquipo && !equiposNuevos.some(e => e.sn === selectedEquipo.sn) && (
                  <Paper sx={{ p: 2, mb: 2, bgcolor: '#e8f5e9', border: '1px solid #4caf50' }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Box>
                        <Typography variant="subtitle2" color="success.main">Equipo seleccionado:</Typography>
                        <Typography variant="body1" fontWeight="bold">{selectedEquipo.sn}</Typography>
                        <Typography variant="caption">{selectedEquipo.modelo || 'Modelo no especificado'}</Typography>
                      </Box>
                      <Button variant="contained" onClick={agregarEquipo} startIcon={<AddIcon />} size="small">
                        Agregar a la lista
                      </Button>
                    </Box>
                  </Paper>
                )}

                <Divider sx={{ my: 1 }} />
                <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>
                  Equipos seleccionados ({equiposNuevos.length}):
                </Typography>
                {equiposNuevos.length === 0 ? (
                  <Typography variant="caption" color="text.secondary">Ningún equipo seleccionado</Typography>
                ) : (
                  equiposNuevos.map((eq, idx) => (
                    <Paper key={idx} sx={{ p: 2, mb: 2 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                        <Typography fontWeight="bold">{eq.sn}</Typography>
                        <IconButton size="small" color="error" onClick={() => eliminarEquipo(idx)}>
                          <DeleteIcon />
                        </IconButton>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Typography variant="caption">{eq.modelo}</Typography>
                        <FormControl size="small" sx={{ minWidth: 120 }}>
                          <InputLabel>Tipo</InputLabel>
                          <Select
                            value={eq.tipo}
                            onChange={(e) => actualizarTipoEquipo(idx, e.target.value)}
                            label="Tipo"
                          >
                            <MenuItem value="ONU">ONU</MenuItem>
                            <MenuItem value="ROUTER">Router</MenuItem>
                            <MenuItem value="BRIDGE">Bridge</MenuItem>
                            <MenuItem value="SWITCH">Switch</MenuItem>
                          </Select>
                        </FormControl>
                      </Box>
                    </Paper>
                  ))
                )}
              </>
            )}
          </AccordionDetails>
        </Accordion>

        {/* Materiales Utilizados */}
        <Accordion expanded={expandedMateriales} onChange={() => setExpandedMateriales(!expandedMateriales)} sx={{ mt: 2 }}>
          <AccordionSummary expandIcon={<ExpandMore />}>
            <Typography variant="h6">
              <Inventory sx={{ mr: 1 }} />
              Materiales Utilizados ({materiales.length})
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            {/* ✅ BOTÓN PARA VER MATERIALES (TEMPORAL) */}
            <Box sx={{ mb: 2 }}>
              <Button
                variant="outlined"
                color="info"
                size="small"
                onClick={() => {
                  console.log('📦 Materiales actuales en estado:', materiales);
                  alert(`Materiales: ${materiales.length}\n${JSON.stringify(materiales, null, 2)}`);
                }}
                sx={{ mr: 1 }}
              >
                Ver Materiales
              </Button>
            </Box>

            <Grid container spacing={2} alignItems="center" sx={{ mb: 2 }}>
              <Grid item xs={12} md={5}>
                <FormControl fullWidth size="small">
                  <InputLabel>Material</InputLabel>
                  <Select
                    value={nuevoMaterial.material_id}
                    onChange={(e) => setNuevoMaterial({ ...nuevoMaterial, material_id: e.target.value })}
                    label="Material"
                  >
                    {catalogoMateriales.map(m => (
                      <MenuItem key={m.id} value={m.id}>{m.nombre} ({m.unidad})</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={2}>
                <TextField
                  fullWidth
                  type="number"
                  label="Cantidad"
                  size="small"
                  value={nuevoMaterial.cantidad}
                  onChange={(e) => setNuevoMaterial({ ...nuevoMaterial, cantidad: parseInt(e.target.value) || 1 })}
                  inputProps={{ min: 1 }}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Observaciones"
                  size="small"
                  value={nuevoMaterial.observaciones}
                  onChange={(e) => setNuevoMaterial({ ...nuevoMaterial, observaciones: e.target.value })}
                  placeholder="Ej: Conector SC/APC"
                />
              </Grid>
              <Grid item xs={12} md={1}>
                <Button
                  fullWidth
                  variant="contained"
                  onClick={agregarMaterial}
                  startIcon={<AddIcon />}
                  disabled={!nuevoMaterial.material_id}
                >
                  Agregar
                </Button>
              </Grid>
            </Grid>

            {materiales.length > 0 && (
              <List sx={{ mb: 2 }}>
                <Divider />
                {materiales.map((mat, idx) => (
                  <React.Fragment key={idx}>
                    <ListItem>
                      <ListItemText
                        primary={`${mat.nombre} x${mat.cantidad}`}
                        secondary={mat.observaciones || 'Sin observaciones'}
                      />
                      <ListItemSecondaryAction>
                        <IconButton edge="end" onClick={() => eliminarMaterial(idx)} size="small">
                          <DeleteIcon />
                        </IconButton>
                      </ListItemSecondaryAction>
                    </ListItem>
                    <Divider />
                  </React.Fragment>
                ))}
              </List>
            )}
          </AccordionDetails>
        </Accordion>

        <TextField
          fullWidth
          label="Motivo del Cambio (opcional)"
          multiline
          rows={2}
          value={motivo}
          onChange={(e) => setMotivo(e.target.value)}
          placeholder="Ej: Equipo dañado por tormenta, actualización a GPON, mejora de velocidad..."
          sx={{ mt: 2, mb: 2 }}
        />

        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={loading}>Cancelar</Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          color="info"
          disabled={loading || equiposNuevos.length === 0 || materiales.length === 0}
          startIcon={loading ? <CircularProgress size={20} /> : <SwapHorizIcon />}
        >
          {loading ? 'Cerrando...' : 'Completar Cambio'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CloseCambioEquipoDialog;
